/**
 * bloomreach-quick-filters.js
 *
 * Toolbar "quick filter" dropdowns for the Bloomreach collections widget —
 * the Bloomreach counterpart of the native #FacetFilterDropdownGroup
 * (snippets/facet-filter-dropdown.liquid, driven by the native section's
 * separated_filter_groups setting: Hauptkategorie / Kategorie / ...).
 *
 * Bloomreach has no such control and its facets live inside <br-facet>
 * shadow roots, so this is a light-DOM bridge in the same style as
 * bloomreach-facets-drawer.js:
 *   - it never moves or re-creates anything Bloomreach rendered;
 *   - it READS a matching facet's options (name, count, checked) through
 *     br-facet.shadowRoot and renders a native-looking <select> of its own;
 *   - on change it CLICKS the real option inside the facet, so filtering,
 *     URL state and counts stay entirely Bloomreach's.
 *
 * Bloomreach swaps the whole toolbar node on every filter change, so the
 * group is a single element that gets re-homed and re-filled after each
 * swap (MutationObserver + debounce), same pattern as the drawer script.
 *
 * Matching facets get .br-facet--quick, which hides them from the drawer
 * from the lg breakpoint up — same as the native drawer (" lg:hidden" in
 * snippets/facets.liquid), where the dropdowns take over.
 *
 * DEBUG MODE: ?brquickdebug=1 (or localStorage br_quick_debug = '1') logs
 * every facet title found, so a non-matching facet name is easy to spot.
 */
(function () {
  var HOST = '.br-widget-host--collections';
  var QUICK_CLASS = 'br-facet--quick';

  // Same labels as the native section's separated_filter_groups setting
  // (templates/collection.*.json), plus the raw Bloomreach field names in
  // case the facet is shown untranslated. Override with
  // window.BRQuickFilterGroups = ['...'] if Bloomreach names them differently.
  var GROUPS = (window.BRQuickFilterGroups || [
    'Hauptkategorie', 'Kategorie', 'Hoofdcategorie', 'Categorie', 'Univers', 'Catégorie',
    'main_category', 'sub_category',
  ]).map(normalize);

  var DEBUG =
    /[?&]brquickdebug=1/.test(window.location.search) ||
    (function () {
      try {
        return window.localStorage.getItem('br_quick_debug') === '1';
      } catch (e) {
        return false;
      }
    })();

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[BR-Quick-Filters]');
    console.log.apply(console, args);
  }

  function normalize(text) {
    return String(text || '').toLowerCase().replace(/[^a-zà-ÿ]/g, '');
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  // ---- Reading Bloomreach's facet ----

  // Bloomreach appends the active-value count to the title once something
  // is checked ("Hauptkategorie (1)"). Strip it: the title is the facet's
  // identity across toolbar swaps — with the count left in, apply() lost the
  // facet as soon as it unchecked the old value (title flips back to
  // "Hauptkategorie") and never checked the new one. Native placeholders
  // show no count either.
  function facetTitle(facetEl) {
    var root = facetEl.shadowRoot;
    var el = root && root.querySelector('.br-facet__title');
    return el ? el.textContent.trim().replace(/\s*\(\d+\)$/, '') : '';
  }

  function isChecked(optionEl) {
    var input = optionEl.querySelector('input[type="checkbox"], input[type="radio"]');
    if (input) return input.checked;
    if (optionEl.getAttribute('aria-checked') === 'true' || optionEl.getAttribute('aria-selected') === 'true') {
      return true;
    }
    return /(^|[\s_-])(selected|active|checked)\b/.test(optionEl.className);
  }

  function readOptions(facetEl) {
    var root = facetEl.shadowRoot;
    if (!root) return [];
    return Array.prototype.map.call(root.querySelectorAll('.br-facet__option'), function (el) {
      var nameEl = el.querySelector('.br-facet__option-name');
      var countEl = el.querySelector('.br-facet__option-count');
      var count = countEl ? parseInt(countEl.textContent.replace(/[^\d]/g, ''), 10) : NaN;
      return {
        el: el,
        name: (nameEl ? nameEl.textContent : el.textContent).trim(),
        count: isNaN(count) ? null : count,
        checked: isChecked(el),
      };
    }).filter(function (option) {
      return option.name;
    });
  }

  // facet_options_limit (5 on the HAY widget) collapses the list behind a
  // "+ N weitere anzeigen" toggle — the dropdown needs every option.
  async function expandShowMore(root) {
    for (var i = 0; i < 10; i++) {
      var more = root.querySelector('.br-facet__show-more');
      if (!more || !/^\s*\+/.test(more.textContent)) return;
      more.click();
      await wait(30);
    }
  }

  // Options may only exist while the facet's dropdown is open. Open it,
  // read everything, and close it again if we were the ones who opened it
  // (unless keepOpen: apply() is about to click one of those options).
  async function collectOptions(facetEl, keepOpen) {
    var root = facetEl.shadowRoot;
    if (!root) return [];

    var openedHere = false;
    if (!root.querySelector('.br-facet__option')) {
      var header = root.querySelector('.br-facet__header');
      if (!header) return [];
      header.click();
      openedHere = true;
      await wait(30);
    }
    await expandShowMore(root);
    var options = readOptions(facetEl);

    if (openedHere && !keepOpen && facetEl.isConnected) {
      var headerAgain = root.querySelector('.br-facet__header');
      if (headerAgain) headerAgain.click();
    }
    return options;
  }

  function clickOption(option) {
    var target = option.el.querySelector('input[type="checkbox"], input[type="radio"]') || option.el;
    target.click();
  }

  // ---- Our own group ----
  //
  // Flash/CLS notes — why this is shaped the way it is:
  //  - The group and its <select>s are created once and only ever *moved*
  //    or *patched*, never rebuilt, so a toolbar swap can't blank them.
  //  - Bloomreach replaces the toolbar on every filter change, which takes
  //    our group out with it. rehome() puts the same node back from inside
  //    the MutationObserver callback — a microtask, i.e. before the next
  //    paint — so the dropdowns never visibly disappear. Only the (slow,
  //    facet-opening) option refresh is debounced.
  //  - apply() checks the new value BEFORE unchecking the old one, so the
  //    chip row never passes through an empty state (collapse + re-grow).
  //  - While a change is in flight the widget's height is pinned, so the
  //    grid swap can't collapse the page and shove the footer around.

  var group = null;
  var dropdowns = {}; // title -> { wrap, select }
  var busy = false;

  function buildGroup() {
    var el = document.createElement('div');
    el.className =
      'facet-filter-dropdown-group br-quick-filters hidden lg:flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-4';
    el.style.display = 'none';
    return el;
  }

  // Same sizing as FacetFilterDropdown.calcSelectWidth (assets/facets.js):
  // the select is as wide as its currently selected option's text.
  function sizeSelect(select) {
    var style = window.getComputedStyle(select);
    var probe = document.createElement('span');
    probe.style.cssText =
      'position:absolute;visibility:hidden;white-space:nowrap;font-family:' +
      style.fontFamily + ';font-size:' + style.fontSize + ';font-weight:' + style.fontWeight;
    probe.textContent = select.options[select.selectedIndex] ? select.options[select.selectedIndex].text : '';
    document.body.appendChild(probe);
    var extra =
      (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0) +
      (parseFloat(style.borderLeftWidth) || 0) + (parseFloat(style.borderRightWidth) || 0);
    select.style.setProperty('--width', probe.offsetWidth + extra + 'px');
    probe.remove();
  }

  function createDropdown(title) {
    var wrap = document.createElement('div');
    wrap.className = 'facet-filter-dropdown flex items-center gap-2';
    wrap.innerHTML =
      '<div class="select w-auto facet-short flex items-center gap-2 flex-grow">' +
      '<select class="form-control form-control--select"></select>' +
      '<svg class="icon icon-caret-down icon--2xs icon--thick" width="18" height="11" viewBox="0 0 18 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><use href="#icon-caret-down"></use></svg>' +
      '</div>';
    var select = wrap.querySelector('select');
    select.setAttribute('aria-label', title);
    select.addEventListener('change', function () {
      sizeSelect(select);
      apply(title, select.value);
    });
    return { wrap: wrap, select: select };
  }

  function fillDropdown(select, title, options) {
    var els = [new Option(title, '')];
    var active = null;
    options.forEach(function (option) {
      var label = option.name + (option.count !== null ? ' (' + option.count + ')' : '');
      var isActive = option.checked && !active;
      var el = new Option(label, option.name, isActive, isActive);
      if (isActive) active = option.name;
      if (option.count === 0 && !option.checked) el.disabled = true;
      els.push(el);
    });
    select.replaceChildren.apply(select, els);
    select.value = active || '';
    sizeSelect(select);
  }

  // Move the (persistent) group back next to our Filter button if a
  // toolbar swap took it out. Cheap and synchronous — safe to call from the
  // MutationObserver callback. That button comes from
  // bloomreach-facets-drawer.js; until it exists, sit before the drawer body.
  function rehome() {
    if (!group) return;
    var host = document.querySelector(HOST);
    if (!host) return;
    var anchor = host.querySelector('.br-facet-drawer-trigger-wrap');
    if (anchor) {
      if (anchor.nextSibling !== group) anchor.parentNode.insertBefore(group, anchor.nextSibling);
      return;
    }
    var body = host.querySelector('.br-toolbar__filters-drawer-body');
    if (body && body.previousSibling !== group) body.parentNode.insertBefore(group, body);
  }

  // ---- Mobile grid/list toggle ----
  //
  // Bloomreach counterpart of the native <layout-switcher class="m-only">
  // (main-collection-product-grid, layout_switcher_mobile_only). Same
  // markup/classes and SVGs, so the theme's .btn--icon/.btn--active styles
  // apply; the mobile-only positioning that lived in collection.css is in
  // theme.css (.br-layout-switcher), since collection.css isn't loaded on
  // Bloomreach templates. Shares the native localStorage key, so the choice
  // carries between native and Bloomreach PLPs. The mode is a class on the
  // widget host (.br-layout--list) — that node is the app block itself and
  // survives Bloomreach's toolbar/grid re-renders, unlike the toolbar.
  var LAYOUT_KEY = 'hypertheme:collection-layout';
  var LAYOUT_LIST_CLASS = 'br-layout--list';
  var STRINGS = window.BRFacetDrawerStrings || {};
  var switcher = null;

  var ICON_GRID =
    '<svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M8.4935 9.21875H1.22507C0.818325 9.21875 0.5 8.89191 0.5 8.4944V1.22435C0.5 0.818009 0.827168 0.5 1.22507 0.5H8.4935C8.90025 0.5 9.21857 0.826843 9.21857 1.22435V8.48556C9.22742 8.89191 8.90025 9.21875 8.4935 9.21875Z" stroke="#333333" stroke-miterlimit="10"/>' +
    '<path d="M8.4935 20.4286H1.22507C0.818325 20.4286 0.5 20.1017 0.5 19.7042V12.4342C0.5 12.0278 0.827168 11.7098 1.22507 11.7098H8.4935C8.90025 11.7098 9.21857 12.0367 9.21857 12.4342V19.6954C9.22742 20.1017 8.90025 20.4286 8.4935 20.4286Z" stroke="#333333" stroke-miterlimit="10"/>' +
    '<path d="M19.7044 9.21875H12.436C12.0293 9.21875 11.7109 8.89191 11.7109 8.4944V1.22435C11.7109 0.818009 12.0381 0.5 12.436 0.5H19.7044C20.1112 0.5 20.4295 0.826843 20.4295 1.22435V8.48556C20.4384 8.89191 20.1112 9.21875 19.7044 9.21875Z" stroke="#333333" stroke-miterlimit="10"/>' +
    '<path d="M19.7044 20.4286H12.436C12.0293 20.4286 11.7109 20.1017 11.7109 19.7042V12.4342C11.7109 12.0278 12.0381 11.7098 12.436 11.7098H19.7044C20.1112 11.7098 20.4295 12.0367 20.4295 12.4342V19.6954C20.4384 20.1017 20.1112 20.4286 19.7044 20.4286Z" stroke="#333333" stroke-miterlimit="10"/>' +
    '</svg>';
  var ICON_LIST =
    '<svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M18.8363 20.5H2.16325C1.23021 20.5 0.5 19.7503 0.5 18.8384V2.1616C0.5 1.22948 1.25049 0.5 2.16325 0.5H18.8363C19.7694 0.5 20.4996 1.24975 20.4996 2.1616V18.8181C20.5199 19.7503 19.7694 20.5 18.8363 20.5Z" stroke="#333333" stroke-miterlimit="10"/>' +
    '</svg>';

  function readLayout() {
    try {
      return window.localStorage.getItem(LAYOUT_KEY) === 'list' ? 'list' : 'grid';
    } catch (e) {
      return 'grid';
    }
  }

  function applyLayout(mode, persist) {
    var host = document.querySelector(HOST);
    if (host) host.classList.toggle(LAYOUT_LIST_CLASS, mode === 'list');
    if (switcher) {
      switcher.querySelectorAll('button').forEach(function (button) {
        var active = button.getAttribute('data-layout-mode') === mode;
        button.classList.toggle('btn--active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }
    if (persist) {
      try {
        window.localStorage.setItem(LAYOUT_KEY, mode);
      } catch (e) {}
    }
  }

  function buildSwitcher() {
    var el = document.createElement('div');
    el.className = 'br-layout-switcher flex items-center gap-2 shrink-0';
    el.innerHTML =
      '<button type="button" class="btn btn--icon btn--icon-circle" data-layout-mode="grid">' +
      '<span class="btn__text flex gap-1 items-center">' + ICON_GRID + '</span></button>' +
      '<button type="button" class="btn btn--icon btn--icon-circle" data-layout-mode="list">' +
      '<span class="btn__text flex gap-1 items-center">' + ICON_LIST + '</span></button>';
    el.querySelector('[data-layout-mode="grid"]').setAttribute('aria-label', STRINGS.layoutGrid || 'Grid');
    el.querySelector('[data-layout-mode="list"]').setAttribute('aria-label', STRINGS.layoutList || 'List');
    el.addEventListener('click', function (event) {
      var button = event.target.closest('button[data-layout-mode]');
      if (button) applyLayout(button.getAttribute('data-layout-mode'), true);
    });
    return el;
  }

  // Right before <br-sorting>, same slot as native (Filter left, toggle
  // centred — absolutely, see theme.css — sorting right).
  function rehomeSwitcher() {
    var host = document.querySelector(HOST);
    var toolbar = host && host.querySelector('.br-toolbar');
    if (!toolbar) return;
    if (!switcher) {
      switcher = buildSwitcher();
      applyLayout(readLayout(), false);
    }
    var sorting = toolbar.querySelector(':scope > br-sorting');
    if (sorting) {
      if (sorting.previousSibling !== switcher) toolbar.insertBefore(switcher, sorting);
    } else if (switcher.parentNode !== toolbar) {
      toolbar.appendChild(switcher);
    }
  }

  function findFacet(title) {
    var host = document.querySelector(HOST);
    if (!host) return null;
    var facets = host.querySelectorAll('br-facet');
    for (var i = 0; i < facets.length; i++) {
      if (facetTitle(facets[i]) === title) return facets[i];
    }
    return null;
  }

  // Bloomreach replaces the facet (and the whole toolbar) after each click;
  // wait for that before reading or clicking the next option.
  async function waitForReplacement(facetEl) {
    for (var i = 0; i < 100 && facetEl.isConnected; i++) await wait(50);
    await wait(50);
  }

  function lockHeight() {
    var host = document.querySelector(HOST);
    if (!host) return null;
    host.style.minHeight = host.offsetHeight + 'px';
    return host;
  }

  // Release once the grid has cards again, so the page never collapses
  // between Bloomreach clearing the old results and painting the new ones.
  async function unlockHeight(host) {
    if (!host) return;
    for (var i = 0; i < 60; i++) {
      var grid = host.querySelector('.br-products__grid');
      if (!grid || grid.children.length) break;
      await wait(50);
    }
    window.requestAnimationFrame(function () {
      host.style.minHeight = '';
    });
  }

  // The native dropdown is single-select, Bloomreach's facet is a checkbox
  // list. Check the chosen value first, then uncheck everything else in the
  // facet (or just uncheck all, for the placeholder option).
  async function apply(title, value) {
    if (busy) return;
    busy = true;
    var host = lockHeight();
    if (group) group.setAttribute('aria-busy', 'true');
    log('apply', title, '→', value || '(none)');

    try {
      for (var guard = 0; guard < 20; guard++) {
        var facetEl = findFacet(title);
        if (!facetEl) break;
        var options = await collectOptions(facetEl, true);
        var toCheck = value && options.filter(function (o) { return o.name === value && !o.checked; })[0];
        var toUncheck = options.filter(function (o) { return o.checked && o.name !== value; })[0];
        var next = toCheck || toUncheck;
        if (!next) break;
        clickOption(next);
        await waitForReplacement(facetEl);
      }
    } catch (err) {
      console.error('[BR-Quick-Filters] apply failed', err);
    } finally {
      busy = false;
      if (group) group.removeAttribute('aria-busy');
      cache.key = null; // force one re-read so checked state/counts are fresh
      schedule();
      unlockHeight(host);
    }
  }

  async function sync() {
    if (busy) return;
    var host = document.querySelector(HOST);
    var body = host && host.querySelector('.br-toolbar__filters-drawer-body');
    if (!body) return;

    var matches = [];
    host.querySelectorAll('br-facet').forEach(function (facetEl) {
      var title = facetTitle(facetEl);
      if (!title) return;
      var isQuick = GROUPS.indexOf(normalize(title)) !== -1;
      log('facet', JSON.stringify(title), isQuick ? '→ quick filter' : '');
      facetEl.classList.toggle(QUICK_CLASS, isQuick);
      if (isQuick) matches.push({ el: facetEl, title: title });
    });

    if (!group) group = buildGroup();
    rehome();
    rehomeSwitcher();

    // Reading a facet means opening it (header click), and Bloomreach
    // re-renders the toolbar when a facet opens — which is itself a DOM
    // mutation that schedules another sync. Re-reading on every sync
    // therefore loops forever. So: only re-read when the results actually
    // changed, and only touch our DOM when what we'd render differs.
    var key = resultsKey(host, matches);
    if (cache.key !== key) {
      var data = [];
      for (var i = 0; i < matches.length; i++) {
        var options = await collectOptions(matches[i].el);
        if (busy) return; // a change started while we were reading
        data.push({
          title: matches[i].title,
          options: options.map(function (o) {
            return { name: o.name, count: o.count, checked: o.checked };
          }),
        });
      }
      cache = { key: key, data: data };
    }

    var signature = JSON.stringify(cache.data);
    if (signature === renderedSignature) return;
    renderedSignature = signature;

    // Patch in place: existing selects keep their node (and focus), only
    // their options change; new facets are appended, vanished ones removed.
    var wanted = cache.data.filter(function (d) { return d.options.length; });
    var keep = {};
    wanted.forEach(function (d, index) {
      var dd = dropdowns[d.title] || (dropdowns[d.title] = createDropdown(d.title));
      keep[d.title] = true;
      if (group.children[index] !== dd.wrap) group.insertBefore(dd.wrap, group.children[index] || null);
      fillDropdown(dd.select, d.title, d.options);
    });
    Object.keys(dropdowns).forEach(function (title) {
      if (!keep[title]) {
        dropdowns[title].wrap.remove();
        delete dropdowns[title];
      }
    });
    // Inline style, not [hidden]: the lg:flex utility class would win over it.
    group.style.display = wanted.length ? '' : 'none';
  }

  var cache = { key: null, data: [] };
  var renderedSignature = null;

  // Changes whenever the result set does: URL (Loomi mirrors filters/sort
  // there), the "N Produkte ansehen" count, the active-filter chips, and
  // which quick facets exist. Opening/closing a facet changes none of these.
  function resultsKey(host, matches) {
    var count = host.querySelector('.br-toolbar__filters-drawer-apply');
    var selected = host.querySelector('.br-facets__selected');
    return [
      window.location.search,
      count ? count.textContent.trim() : '',
      selected ? selected.textContent.trim() : '',
      matches.map(function (m) { return m.title; }).join('|'),
    ].join('#');
  }

  var timer = null;
  var syncing = false;
  var pending = false;

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(run, 120);
  }

  async function run() {
    if (syncing) {
      pending = true;
      return;
    }
    syncing = true;
    try {
      await sync();
    } catch (err) {
      console.error('[BR-Quick-Filters] sync failed', err);
    } finally {
      syncing = false;
      if (pending) {
        pending = false;
        schedule();
      }
    }
  }

  function boot() {
    rehomeSwitcher();
    schedule();
    new MutationObserver(function (mutations) {
      // Put the group (and the layout toggle) straight back before the
      // browser paints the swap...
      rehome();
      rehomeSwitcher();
      // ...and refresh its options later, ignoring mutations of our own.
      for (var i = 0; i < mutations.length; i++) {
        var target = mutations[i].target;
        var ours = (group && group.contains(target)) || (switcher && switcher.contains(target));
        if (!ours) return schedule();
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
